import { GraphQLError } from 'graphql';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GenericFormScene } from 'shared-ui/lib/components/generic-form-scene/GenericFormScene';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { parseGraphqlErrors } from 'shared-ui/lib/utils/get-gql-error';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { routesConfig } from '../../config/routes.config';
import { groupForm, GroupFormBody } from '../../form/group/group.form';
import {
  useDeleteGroupMutation,
  useGroupEditSceneQuery,
  useSetGroupUsersMutation,
  useSetGroupVehiclesMutation,
  useUpdateGroupMutation,
} from '../../generated/graphql';

export const DefaultGroupRulesNotice = (): JSX.Element => {
  const { t } = useTranslation('GroupEditScene');

  return (
    <div className="mt-3 text-sm text-gray-700">
      <p>{t('defaultGroupInfo')}</p>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        <li>{t('defaultGroupRuleName')}</li>
        <li>{t('defaultGroupRuleVehicles')}</li>
        <li>{t('defaultGroupRuleMaster')}</li>
      </ul>
    </div>
  );
};

export const GroupEditScene = (): JSX.Element => {
  const { t } = useTranslation('GroupEditScene');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const query = useGroupEditSceneQuery(setupQueryConfig({ id }));
  const [updateGroup, { loading: isUpdateLoading }] = useUpdateGroupMutation();
  const [setGroupVehicles, { loading: isVehiclesLoading }] = useSetGroupVehiclesMutation();
  const [setGroupUsers, { loading: isUsersLoading }] = useSetGroupUsersMutation();
  const [deleteGroup, { loading: isDeleteLoading }] = useDeleteGroupMutation({
    variables: { id },
    onCompleted: (data) => {
      if (data) navigate(routesConfig.SETTINGS.GROUPS.INDEX);
    },
  });

  const isDefault = !!query.data?.group.isDefault;

  const handleSubmit = async (body: GroupFormBody) => {
    try {
      const updateBody: { name?: string; notificationEmail?: string | null } = {
        notificationEmail: body.notificationEmail ?? null,
      };
      if (!isDefault) {
        updateBody.name = body.name;
      }

      const { data, errors } = await updateGroup({ variables: { id, body: updateBody } });
      if (!data) throw new Error(parseGraphqlErrors(errors as GraphQLError[]));

      await setGroupVehicles({
        variables: { id, body: { vehicleIds: body.vehicleIds ?? [] } },
      });
      await setGroupUsers({
        variables: { id, body: { userIds: body.userIds ?? [] } },
      });

      toast.success(t('General:updated'));
      navigate(routesConfig.SETTINGS.GROUPS.INDEX);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const handleDelete = () => {
    if (confirm(t('confirmDelete'))) deleteGroup();
  };

  return (
    <GenericFormScene
      type="edit"
      query={query}
      queryDataPath="group"
      title={t('editGroup')}
      headerExtra={isDefault ? <DefaultGroupRulesNotice /> : undefined}
      onSubmit={handleSubmit}
      form={groupForm}
      backLink={routesConfig.SETTINGS.GROUPS.INDEX}
      onDelete={isDefault ? undefined : handleDelete}
      isSubmitLoading={isUpdateLoading || isVehiclesLoading || isUsersLoading || isDeleteLoading}
      onSubmitValidationFailed={() => toast.error(t('notSavedCheckFields'))}
    />
  );
};
